import React, {useState} from 'react';

import {useUserRoles} from 'src/hooks/useUserRoles';
import {EnhanceCommentController} from 'src/modules/ai/comment/enhance-comment.controller';
import {Comment} from 'src/modules/comments/components/comment/comment';
import {HtmlArea} from 'src/modules/comments/components/html-area/html-area';
import {MAX_COMMENT_HTML_LENGTH} from 'src/modules/comments/constants';
import {ChangeRequest} from 'src/openapi/models/ChangeRequest';
import {ChangeRequestComment} from 'src/openapi/models/ChangeRequestComment';
import {Role} from 'src/openapi/models/Role';
import {hasEditRole} from 'src/utils/rolePermissions';

import Button from '@pb/kit-component-button';
import {Group, Item} from '@pb/kit-component-group';
import {Tooltip} from '@pb/kit-component-popups';
import {Scroll} from '@pb/kit-component-scroll';

import {commentsBlockMessages, commentsBlockScroll} from './comments.module.styl';

type Props = {
    userEmail: string,
    comments: ChangeRequestComment[],
    currentComment: string,
    changeRequestId: number | null,
    changeRequest?: ChangeRequest,
    editedCommentId: number | null,
    onAddComment: () => void,
    onChange: (value: string) => void,
    onEditComment: (comment: ChangeRequestComment) => void,
    onCancel: () => void,
    onUpdateComment: () => void,
};

const SAVE_COMMENT_TOOLTIP = 'Please, save your change request before commenting';

export const CommentsView = (props: Props) => {
    const {
        userEmail,
        comments,
        changeRequestId,
        currentComment,
        changeRequest,
        editedCommentId,
        onAddComment,
        onChange,
        onEditComment,
        onCancel,
        onUpdateComment,
    } = props;
    const userRoles = useUserRoles();
    const [commentTextLength, setCommentTextLength] = useState(0);
    const [commentHtmlLength, setCommentHtmlLength] = useState(0);

    const isHtmlOverLimit = commentHtmlLength > MAX_COMMENT_HTML_LENGTH;
    const isAddDisabled = !commentTextLength || isHtmlOverLimit;

    return (
        <Group direction="v" gap="xxs">
            <strong>Comments</strong>
            <Item style={{width: '100%'}}>
                {!comments.length && changeRequest && changeRequest.authorEmail !== userEmail
                    && !userRoles.includes(Role.ROLE_UDEDT_REVIEWER) && !userRoles.includes(Role.ROLE_UDEDT_APPROVER) ?
                        <span>No comments</span> : (
                            <Group direction="v">
                                {comments.length ? (
                                    <Item className={`${commentsBlockMessages} pdx-s pdy-s`} style={{width: '100%'}}>
                                        <Scroll className={commentsBlockScroll}>
                                            <Group
                                                direction="v"
                                                alignItems="start"
                                                justify="end">
                                                {comments.map(comment =>
                                                    (
                                                        <Item key={comment.id} style={{width: '100%'}}>
                                                            <Comment
                                                                comment={comment}
                                                                changeRequest={changeRequest}
                                                                userEmail={userEmail}
                                                                onEditComment={onEditComment}
                                                                editedCommentId={editedCommentId}
                                                            />
                                                        </Item>
                                                    ),
                                                )}
                                            </Group>
                                        </Scroll>
                                    </Item>
                                ) : null}
                                {(!changeRequest || changeRequest.authorEmail === userEmail || hasEditRole(userRoles))
                                    && (
                                        <Item style={{width: '100%'}}>
                                            <Group direction="v" alignItems="end">
                                                <Item style={{width: '100%'}}>
                                                    <HtmlArea
                                                        changeRequestId={changeRequestId}
                                                        currentComment={currentComment}
                                                        onChange={onChange}
                                                        onHtmlLengthChange={setCommentHtmlLength}
                                                        onTextLengthChange={setCommentTextLength}
                                                    />
                                                </Item>
                                                <Group>
                                                    {userRoles.includes(Role.ROLE_UDEDT_REVIEWER) && (
                                                        <EnhanceCommentController
                                                            comment={currentComment}
                                                            disabled={isAddDisabled}
                                                            onChange={onChange}
                                                        />
                                                    )}
                                                    {editedCommentId ? (
                                                        <Group>
                                                            <Button onClick={onUpdateComment} variant="Type.PRIMARY">
                                                                Update Comment
                                                            </Button>
                                                            <Button onClick={onCancel} variant="Type.SECONDARY">
                                                                Cancel
                                                            </Button>
                                                        </Group>
                                                    ) : (
                                                        <Group>
                                                            <Tooltip
                                                                anchor={({anchorRef}) => (
                                                                    <Button
                                                                        ref={anchorRef}
                                                                        onClick={onAddComment}
                                                                        disabled={isAddDisabled}
                                                                        variant="Type.PRIMARY">
                                                                        Add Comment
                                                                    </Button>
                                                                )}>
                                                                {!changeRequestId && SAVE_COMMENT_TOOLTIP}
                                                            </Tooltip>
                                                        </Group>
                                                    )}
                                                </Group>
                                            </Group>
                                        </Item>
                                    )}
                            </Group>
                        )}
            </Item>
        </Group>
    );
};
